/**
 * Client-side growth loop helpers: capture a referral/UTM source on landing, then (after the referee's
 * first login post-signup) convert the referral and record attribution. All calls are fire-and-forget
 * and best-effort; growth is never allowed to block auth or the app. The server enforces anti-fraud +
 * idempotency, so retries/duplicates are safe.
 */
import { api } from './api.js';

const REF_KEY = 'tl_ref';
const SRC_KEY = 'tl_src';
const JUST_REGISTERED = 'tl_just_registered';
const DEVICE_KEY = 'tl_did';

/** Stable per-browser id used as a soft anti-fraud signal for referral conversions. */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

/** On landing, remember a `?ref=` referral code and/or `utm_source` so signup can attribute them. */
export function captureReferral(search: string): void {
  try {
    const params = new URLSearchParams(search);
    const ref = params.get('ref');
    if (ref) localStorage.setItem(REF_KEY, ref.slice(0, 64));
    const src = params.get('utm_source') ?? params.get('src');
    if (src) localStorage.setItem(SRC_KEY, src.slice(0, 64));
  } catch {
    // ignore malformed query strings
  }
}

/** Mark that this browser just created an account, so the next login runs the one-time convert. */
export function markRegistered(): void {
  localStorage.setItem(JUST_REGISTERED, '1');
}

/**
 * Runs after a successful login. Always records a `login` activity (which pays the deferred referrer
 * reward on the referee's first login). If this browser just registered, also converts the stored
 * referral and records the signup source, then clears the one-shot flags.
 */
export function runPostLoginGrowth(): void {
  void api.attributionActivity('login').catch(() => undefined);
  if (localStorage.getItem(JUST_REGISTERED) !== '1') return;
  const ref = localStorage.getItem(REF_KEY);
  const src = ref ? 'referral' : (localStorage.getItem(SRC_KEY) ?? 'direct');
  void api.attributionSignup(src).catch(() => undefined);
  if (ref) void api.referralConvert(ref, deviceId()).catch(() => undefined);
  localStorage.removeItem(JUST_REGISTERED);
  localStorage.removeItem(REF_KEY);
  localStorage.removeItem(SRC_KEY);
}
